import AbstractComponent from "./abstract-component";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { whenIntroStart, whenSiteReady } from "../utils/site-ready";

gsap.registerPlugin(ScrollTrigger);

const DESKTOP_BREAKPOINT = 1190;
// Translate max de l'image (% de sa hauteur) : doit rester <= à la marge
// basse d'overscan définie en SCSS (16% => ~13% de translate sûr).
const PARALLAX_SHIFT = 13;

export default class Header extends AbstractComponent {

    constructor(el) {
        super(el)
        this.DOM = {};
        this.initDomElements();
        this.initEvents();
        this.soundControls = document.querySelectorAll('.js-sound-control');
        this.videos = document.querySelectorAll('video');
        this.initSoundControl();
        this.initScrollControl();
        this.initHeroIntro();
        this.initParallax();
    }

    initParallax() {
        this.parallaxImg = this.$el.querySelector('[data-hero-parallax]');
        if (!this.parallaxImg || window.innerWidth < DESKTOP_BREAKPOINT) return;

        // Hero = première section : le scroll démarre à 0, donc on part de la
        // position naturelle (start "top top") et l'image glisse plus lentement
        // vers le bas -> la section suivante semble passer par-dessus le header.
        this.parallaxTrigger = gsap.fromTo(
            this.parallaxImg,
            { yPercent: 0 },
            {
                yPercent: PARALLAX_SHIFT,
                ease: 'none',
                scrollTrigger: {
                    trigger: this.$el,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: true,
                },
            }
        ).scrollTrigger;
    }

    initHeroIntro() {
        this.covers = this.$el.querySelectorAll('.c-header__cover img');
        this.cta = this.$el.querySelector('.c-header__cta');

        if (!this.covers.length && !this.cta) return;

        // État initial fixé tout de suite pour éviter tout flash.
        // Zoom-out du fond (façon Weavenn) + CTA qui monte en fondu.
        if (this.covers.length) {
            gsap.set(this.covers, { scale: 1.3, transformOrigin: 'center center' });
        }
        if (this.cta) {
            gsap.set(this.cta, { opacity: 0, y: 30 });
        }

        this.playCta = () => {
            if (!this.cta || this._ctaPlayed) return;
            this._ctaPlayed = true;

            gsap.to(this.cta, {
                opacity: 1,
                y: 0,
                ease: 'expo.out',
                duration: 0.9,
                onComplete: () => {
                    this.cta.classList.add('is-visible');
                    gsap.set(this.cta, { clearProps: 'opacity,transform' });
                },
            });
        };

        whenIntroStart(() => {
            if (this.covers.length) {
                gsap.to(this.covers, {
                    scale: 1,
                    ease: 'circ.out',
                    duration: 1.4,
                });
            }
        });

        document.addEventListener('meral:header-text-revealed', () => this.playCta(), { once: true });

        whenSiteReady(() => {
            if (!this.$el.querySelector('[data-split-reveal]')) {
                this.playCta();
            }
        });
	}

    initDomElements() {
        this.videos = this.$el.querySelectorAll('video');
    }

    initEvents() {
        // Use a single event listener with passive option for better performance
        const playVideoHandler = this.playVideo.bind(this);
        document.addEventListener('click', playVideoHandler, { passive: true });
        document.addEventListener('touchstart', playVideoHandler, { passive: true });
        // Remove redundant event listeners that could cause multiple triggers
        // document.addEventListener('touchmove', playVideoHandler);
        // document.addEventListener('scroll', playVideoHandler);
        // document.addEventListener('keydown', playVideoHandler);
    }

    playVideo() {
        // Add a small debounce to prevent multiple rapid calls
        if (this.playVideoTimeout) {
            clearTimeout(this.playVideoTimeout);
        }
        
        this.playVideoTimeout = setTimeout(() => {
            this.videos.forEach(video => {
                if (video.paused) {
                    video.play().catch(error => {
                        console.warn('Video play failed:', error);
                    });
                }
            });
        }, 100);
    }

    initSoundControl() {
        this.soundControls.forEach(control => {
            control.addEventListener('click', (e) => {
                const isMuted = this.videos[0].muted;
                
                this.videos.forEach(video => {
                    video.muted = !isMuted;
                });
                
                e.currentTarget.classList.toggle('is-muted');
            });
        });
    }

    initScrollControl() {
        const scrollBtn = this.$el.querySelector('.js-header-scroll');
        if (!scrollBtn) return;

        scrollBtn.addEventListener('click', () => {
            const nextSection = this.$el.nextElementSibling;
            const target = nextSection || document.querySelector('[data-scroll-section]:not(.c-header)');

            if (target) {
                if (this.app?.$scroll) {
                    this.app.$scroll.scrollTo(target);
                } else {
                    target.scrollIntoView({ behavior: 'smooth' });
                }
            }
        });
    }

    destroy() {
        // Clean up event listeners and timeouts
        if (this.playVideoTimeout) {
            clearTimeout(this.playVideoTimeout);
        }
        this.parallaxTrigger?.kill();
    }
}
